/**
 * campaignTracking — Corvana's first-party funnel tracker (iter Campaign Intel)
 * ============================================================================
 * Captures the full public journey — page views, every button/link click
 * (with x/y for heatmaps), scroll depth and form start/abandon/submit — and
 * ties it back to the prospect + campaign via the signed `ct` attribution
 * token carried from the outreach email.
 *
 * Fail-soft: tracking must NEVER break the page. All network is batched and
 * flushed via sendBeacon on unload.
 */
import { API_URL } from '@/config/apiConfig';

const VID_KEY = 'cv_vid';
const SID_KEY = 'cv_sid';
const CT_KEY = 'cv_ct';

let queue = [];
let flushTimer = null;
let scrollMilestones = new Set();
let installed = false;

const uid = (p) => `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;

const getVisitorId = () => {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) { v = uid('v'); localStorage.setItem(VID_KEY, v); }
    return v;
  } catch { return uid('v'); }
};

const getSessionId = () => {
  try {
    let s = sessionStorage.getItem(SID_KEY);
    if (!s) { s = uid('s'); sessionStorage.setItem(SID_KEY, s); }
    return s;
  } catch { return uid('s'); }
};

const getCt = () => {
  try { return localStorage.getItem(CT_KEY) || null; } catch { return null; }
};

const captureCtFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const ct = params.get('ct');
    if (ct) {
      localStorage.setItem(CT_KEY, ct);
      // Tell the backend to bind this visitor → prospect for the whole journey.
      navigator.sendBeacon?.(
        `${API_URL}/api/track/identify`,
        new Blob([JSON.stringify({ ct, visitor_id: getVisitorId() })], { type: 'application/json' }),
      );
    }
  } catch { /* ignore */ }
};

const deviceType = () => {
  const ua = navigator.userAgent || '';
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'Tablet';
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry/i.test(ua)) return 'Mobile';
  return 'Desktop';
};

const flush = (useBeacon = false) => {
  if (!queue.length) return;
  const payload = {
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    ct: getCt(),
    device_type: deviceType(),
    referrer: document.referrer || '(direct)',
    events: queue.splice(0, 50),
  };
  const url = `${API_URL}/api/track/ev`;
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch { /* ignore */ }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTimer = null; flush(); }, 2500);
};

/** Queue a single event. */
export const track = (type, payload = {}) => {
  try {
    queue.push({
      type,
      page: window.location.pathname,
      ts: new Date().toISOString(),
      ...payload,
    });
    if (queue.length >= 15) flush();
    else scheduleFlush();
  } catch { /* ignore */ }
};

export const trackPageView = () => {
  scrollMilestones = new Set();
  track('page_view');
};

export const trackEvent = (type, meta = {}) => track(type, { meta });

// ── Element label resolution for heatmaps + CTA ranking ──────────────────
const labelFor = (el) => {
  if (!el) return null;
  const testid = el.getAttribute?.('data-testid');
  if (testid) return testid;
  const aria = el.getAttribute?.('aria-label');
  if (aria) return aria.slice(0, 60);
  const text = (el.innerText || el.textContent || '').trim().replace(/\s+/g, ' ');
  if (text) return text.slice(0, 60);
  return el.tagName ? el.tagName.toLowerCase() : null;
};

const onGlobalClick = (e) => {
  try {
    const target = e.target?.closest?.('a, button, [role="button"], [data-testid]');
    if (!target) return;
    const tag = target.tagName;
    const isInteractive = tag === 'A' || tag === 'BUTTON' || target.getAttribute('role') === 'button';
    const vw = window.innerWidth || 1;
    const docH = Math.max(document.documentElement.scrollHeight, 1);
    const xpct = Math.min(1, Math.max(0, e.clientX / vw));
    const ypct = Math.min(1, Math.max(0, (e.clientY + window.scrollY) / docH));
    track(isInteractive ? 'cta_click' : 'click', {
      element: labelFor(target),
      href: target.getAttribute?.('href') || null,
      tag: isInteractive ? tag : (target.tagName || null),
      xpct, ypct,
    });
  } catch { /* ignore */ }
};

const onScroll = () => {
  try {
    const h = document.documentElement;
    const denom = (h.scrollHeight - h.clientHeight) || 1;
    const pct = Math.round((h.scrollTop / denom) * 100);
    [25, 50, 75, 100].forEach((m) => {
      if (pct >= m && !scrollMilestones.has(m)) {
        scrollMilestones.add(m);
        track('scroll_depth', { scroll_pct: m });
      }
    });
  } catch { /* ignore */ }
};

let scrollRaf = null;
const throttledScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => { scrollRaf = null; onScroll(); });
};

/** Install global listeners. Idempotent. Skips authenticated app surfaces. */
export const initCampaignTracking = () => {
  if (installed || typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path.startsWith('/app') || path.startsWith('/tech-admin') || path.startsWith('/corvana-crm')) {
    // Still capture the attribution token if present, but don't instrument
    // the internal admin surfaces.
    captureCtFromUrl();
    return;
  }
  installed = true;
  captureCtFromUrl();
  document.addEventListener('click', onGlobalClick, true);
  window.addEventListener('scroll', throttledScroll, { passive: true });
  window.addEventListener('pagehide', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  trackPageView();
};
